import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useNavigate } from "react-router-dom";
import { Button, Typography } from "@/components/atoms";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { useProviderCode } from "@/hooks";
import { useAppResetApi, useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { clsx } from "@/utils";
import { SidebarLogo } from "../Sidebar/SidebarLogo";
import { GeneralLogo } from "./GeneralLogo";

export interface GeneralHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  action?: "signUp" | "login" | "contact" | "logout" | "skip";
  isPrivateContent?: boolean;
}

export const GeneralHeader = ({ action, isPrivateContent, className, ...props }: GeneralHeaderProps) => {
  const resetApi = useAppResetApi();
  const { logout } = useKindeAuth();
  const navigate = useNavigate();
  const phpHostUrl = getPhpHostUrl();
  const providerCode = useProviderCode();
  const profile = useAppSelector(profileSelector);
  const operator = useAppSelector(activeOperatorSelector);
  // Only show signup button if operator is single tenant
  const signUpEnabled = operator && operator.singleTenant;

  const handleLogout = async () => {
    resetApi();

    // In production, redirect to PHP logout which handles both PHP session and Kinde logout
    if (import.meta.env.MODE === "production") {
      window.location.href = `${phpHostUrl}/logout`;
    } else {
      // In development, just do Kinde logout
      await logout();
    }
  };

  const renderAction = () => {
    switch (action) {
      case "signUp":
        if (signUpEnabled) {
          return (
            <div className="flex flex-row items-center gap-x-4">
              <Typography className="hidden md:block">Don&apos;t have an account?</Typography>
              <Button variant="primary" size="md" onClick={() => navigate(`/signup${providerCode ? `/${providerCode}` : ""}`)}>
                Sign Up
              </Button>
            </div>
          );
        }
        break;
      case "login":
        return (
          <div className="flex flex-row items-center gap-x-4">
            <Typography className="hidden md:block">Already have an account?</Typography>
            <Button variant="secondary" size="md" onClick={() => navigate("/login")}>
              Login
            </Button>
          </div>
        );
      case "logout":
        return (
          <div className="flex flex-row items-center gap-x-5">
            <Button variant="secondary" size="md" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        );
      case "contact":
        return (
          <div className="flex flex-row items-center gap-x-4">
            <Typography className="hidden md:block">
              Contact us at{" "}
              <a href="mailto:support@rideminder.com" className="text-primary underline">
                support@rideminder.com
              </a>
            </Typography>
          </div>
        );
      case "skip":
        return (
          <div className="flex flex-row items-center">
            <a href={`${phpHostUrl}/passenger/${profile.id}/booking/history`}>
              <Button variant="secondary" size="md">
                Skip for now
              </Button>
            </a>
          </div>
        );
    }
    return null;
  };

  return (
    <header
      className={clsx(
        "relative top-0 flex items-center justify-center border-x-0 border-y-[1px] border-neutral-mid-gray bg-white p-2",
        { "py-6": !isPrivateContent },
        className
      )}
      {...props}
    >
      <div className="flex w-full max-w-[1040px] flex-row">
        {isPrivateContent ? <SidebarLogo ignoreSidebarOpen className="-ml-4 flex flex-1" /> : <GeneralLogo />}
        {renderAction()}
      </div>
    </header>
  );
};
